import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PictureAsPdfRounded,
  CloudUploadRounded,
  ArrowBackRounded,
  RefreshRounded,
  CheckCircleRounded,
  ErrorOutlineRounded,
  HourglassTopRounded,
} from '@mui/icons-material';
import {
  extractPdfTopics,
  listPdfExtractions,
  getPdfExtraction,
  listPdfAssignments,
  replacePdfAssignments,
  PdfExtraction,
  PdfExtractionSummary,
  PdfAssignmentInfo,
} from '../services/api';
import { TeacherClassItem } from '../types';
import { useTeacherAuth } from '../context/TeacherAuthContext';

const classKey = (c: { grade: number; section?: string | null }) =>
  `${c.grade}__${c.section ?? ''}`;
const classLabel = (c: { grade: number; section?: string | null }) =>
  `Grade ${c.grade}${c.section ? ` · Section ${c.section}` : ''}`;

const PdfTopicExtractor: React.FC = () => {
  const navigate = useNavigate();
  const { token, teacher } = useTeacherAuth();

  const [file, setFile] = useState<File | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [current, setCurrent] = useState<PdfExtraction | null>(null);

  const [history, setHistory] = useState<PdfExtractionSummary[] | null>(null);
  const [historyError, setHistoryError] = useState<string | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Assignment panel state
  const [selectedClassKeys, setSelectedClassKeys] = useState<Set<string>>(new Set());
  const [serverAssignments, setServerAssignments] = useState<PdfAssignmentInfo[]>([]);
  const [isLoadingAssignments, setIsLoadingAssignments] = useState(false);
  const [assignError, setAssignError] = useState<string | null>(null);
  const [isSavingAssignments, setIsSavingAssignments] = useState(false);
  const [assignSavedAt, setAssignSavedAt] = useState<number | null>(null);

  // Which section indices the teacher has ticked to send. Defaults to "all" when
  // a fresh extraction is loaded; pre-fills from server when re-opening one.
  const [selectedSectionIdxs, setSelectedSectionIdxs] = useState<Set<number>>(new Set());

  const teacherClasses: TeacherClassItem[] = teacher?.classes ?? [];

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const fetchHistory = useCallback(async () => {
    if (!token) return;
    setIsLoadingHistory(true);
    setHistoryError(null);
    try {
      const rows = await listPdfExtractions(token, { limit: 50 });
      setHistory(rows);
    } catch (err) {
      setHistoryError(err instanceof Error ? err.message : 'Failed to load history.');
    } finally {
      setIsLoadingHistory(false);
    }
  }, [token]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  // Initialize the section-selection checkboxes whenever the open extraction changes.
  useEffect(() => {
    if (!current || current.status !== 'completed' || !Array.isArray(current.sections)) {
      setSelectedSectionIdxs(new Set());
      return;
    }
    const total = current.sections.length;
    if (current.selected_section_indices && Array.isArray(current.selected_section_indices)) {
      setSelectedSectionIdxs(new Set(current.selected_section_indices));
    } else {
      // null on the server = all sections selected
      setSelectedSectionIdxs(new Set(Array.from({ length: total }, (_, i) => i)));
    }
  }, [current]);

  // Fetch existing assignments whenever the open extraction changes.
  useEffect(() => {
    if (!current || !token || current.status !== 'completed') {
      setServerAssignments([]);
      setSelectedClassKeys(new Set());
      return;
    }
    let cancelled = false;
    setIsLoadingAssignments(true);
    setAssignError(null);
    listPdfAssignments(token, current.id)
      .then((rows) => {
        if (cancelled) return;
        setServerAssignments(rows);
        setSelectedClassKeys(new Set(rows.map((r) => classKey(r))));
      })
      .catch((err) => {
        if (!cancelled) setAssignError(err instanceof Error ? err.message : 'Failed to load assignments');
      })
      .finally(() => {
        if (!cancelled) setIsLoadingAssignments(false);
      });
    return () => {
      cancelled = true;
    };
  }, [current, token]);

  const toggleClassKey = (k: string) => {
    setSelectedClassKeys((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  const toggleSectionIdx = (idx: number) => {
    setSelectedSectionIdxs((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  };

  const handleSaveAssignments = async () => {
    if (!current || !token) return;
    const targets = teacherClasses.filter((c) => selectedClassKeys.has(classKey(c)));
    const total = Array.isArray(current.sections) ? current.sections.length : 0;
    // If every section is ticked, send null (= "all") so the server stores it canonically.
    const indices =
      selectedSectionIdxs.size === total
        ? null
        : Array.from(selectedSectionIdxs).sort((a, b) => a - b);

    setIsSavingAssignments(true);
    setAssignError(null);
    try {
      const updated = await replacePdfAssignments(token, current.id, targets, indices);
      setServerAssignments(updated);
      setAssignSavedAt(Date.now());
      // Reflect the saved selection locally on `current` so reload-from-history works without an extra round-trip.
      setCurrent((c) => (c ? { ...c, selected_section_indices: indices } : c));
    } catch (err) {
      setAssignError(err instanceof Error ? err.message : 'Failed to save assignments');
    } finally {
      setIsSavingAssignments(false);
    }
  };

  // Show a green "Saved" hint for ~2 seconds after a successful save.
  const showSavedToast = useMemo(() => {
    if (!assignSavedAt) return false;
    return Date.now() - assignSavedAt < 2500;
  }, [assignSavedAt]);

  const handleFile = (picked: File | null | undefined) => {
    setUploadError(null);
    if (!picked) {
      setFile(null);
      return;
    }
    if (!picked.name.toLowerCase().endsWith('.pdf')) {
      setUploadError('Please choose a .pdf file.');
      return;
    }
    setFile(picked);
  };

  const handleSubmit = async () => {
    if (!file || !token) return;
    setUploadError(null);
    setIsUploading(true);
    setCurrent(null);
    try {
      const result = await extractPdfTopics(token, file);
      setCurrent(result);
      fetchHistory();
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Extraction failed.');
    } finally {
      setIsUploading(false);
    }
  };

  const handleOpenHistoryItem = async (id: number) => {
    if (!token) return;
    setCurrent(null);
    setUploadError(null);
    try {
      const row = await getPdfExtraction(token, id);
      setCurrent(row);
    } catch (err) {
      setUploadError(err instanceof Error ? err.message : 'Failed to load extraction.');
    }
  };

  const sectionCount = useMemo(
    () => (current?.sections && Array.isArray(current.sections) ? current.sections.length : 0),
    [current]
  );

  const statusBadge = (status: PdfExtractionSummary['status']) => {
    if (status === 'completed') {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-700">
          <CheckCircleRounded fontSize="inherit" /> ready
        </span>
      );
    }
    if (status === 'failed') {
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
          <ErrorOutlineRounded fontSize="inherit" /> failed
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
        <HourglassTopRounded fontSize="inherit" /> running
      </span>
    );
  };

  return (
    <div className="main-gradient-bg min-h-screen px-4 py-8 sm:px-6 sm:py-12">
      <div className="mx-auto w-full max-w-5xl space-y-6">
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              <PictureAsPdfRounded /> PDF Topic Extractor
            </h1>
            <p className="text-sm text-slate-600 mt-1">
              Upload a PDF; the AI breaks it into topic sections. Assign to your classes
              so students see the topic when they log in.
            </p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/teacher-view')}
            className="flex items-center gap-1 text-sm font-medium text-slate-500 hover:text-violet-700"
          >
            <ArrowBackRounded fontSize="small" /> Back
          </button>
        </header>

        {/* Upload card */}
        <section className="rounded-3xl border border-violet-200 bg-white/95 p-6 shadow-sm">
          <label
            htmlFor="pdf-file-input"
            className="flex flex-col items-center justify-center gap-3 cursor-pointer rounded-2xl border-2 border-dashed border-violet-300 bg-violet-50/50 px-6 py-10 text-center hover:bg-violet-50 transition-colors"
          >
            <CloudUploadRounded style={{ fontSize: 42 }} className="text-violet-500" />
            <span className="text-sm font-medium text-slate-700">
              {file ? file.name : 'Click to choose a PDF (or drag & drop)'}
            </span>
            <span className="text-xs text-slate-500">Max 25 MB</span>
            <input
              ref={fileInputRef}
              id="pdf-file-input"
              type="file"
              accept="application/pdf,.pdf"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
            />
          </label>

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={!file || isUploading}
              className="rounded-xl bg-violet-500 px-4 py-2 text-sm font-medium text-white hover:bg-violet-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isUploading ? 'Extracting…' : 'Extract topics'}
            </button>
            {file && !isUploading && (
              <button
                type="button"
                onClick={() => {
                  setFile(null);
                  if (fileInputRef.current) fileInputRef.current.value = '';
                }}
                className="text-sm text-slate-500 hover:text-slate-700"
              >
                Clear
              </button>
            )}
            {isUploading && (
              <span className="text-xs text-slate-500">
                Working on it — usually 5–20s depending on the PDF size.
              </span>
            )}
          </div>

          {uploadError && (
            <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {uploadError}
            </div>
          )}
        </section>

        {/* Current result */}
        {current && (
          <section className="rounded-3xl border border-violet-200 bg-white/95 p-6 shadow-sm space-y-4">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <h2 className="text-lg font-semibold text-slate-900">{current.file_name}</h2>
                <p className="text-xs text-slate-500">
                  {current.page_count != null ? `${current.page_count} pages · ` : ''}
                  {sectionCount} sections · extraction #{current.id}
                </p>
              </div>
              {statusBadge(current.status)}
            </div>

            {current.status === 'failed' && current.error_message && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {current.error_message}
              </div>
            )}

            {current.status === 'completed' && Array.isArray(current.sections) && (
              <>
                <p className="text-xs text-slate-500">
                  Tick the sections you want to send to students. Will send{' '}
                  <span className="font-medium text-slate-700">{selectedSectionIdxs.size}</span>{' '}
                  of {current.sections.length} sections.
                </p>
                <ol className="space-y-3">
                  {current.sections.map((s, idx) => {
                    const checked = selectedSectionIdxs.has(idx);
                    return (
                      <li
                        key={`${current.id}-${idx}`}
                        className={`rounded-2xl border p-4 transition-colors ${
                          checked ? 'border-indigo-300 bg-white' : 'border-slate-200 bg-slate-50 opacity-60'
                        }`}
                      >
                        <label className="flex items-start gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleSectionIdx(idx)}
                            className="mt-1 h-4 w-4 accent-indigo-600"
                          />
                          <div className="flex-1">
                            <h3 className="font-semibold text-slate-800">
                              {idx + 1}. {s.title}
                            </h3>
                            <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{s.content}</p>
                          </div>
                        </label>
                      </li>
                    );
                  })}
                </ol>
              </>
            )}

            {/* Assign-to-classes panel */}
            {current.status === 'completed' && (
              <div className="mt-2 rounded-2xl border border-indigo-200 bg-indigo-50/40 p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-slate-800">Assign to your classes</h3>
                  {serverAssignments.length > 0 && (
                    <span className="text-xs text-slate-500">
                      Currently assigned to {serverAssignments.length} class
                      {serverAssignments.length === 1 ? '' : 'es'}
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-600">
                  Tick the classes that should see this topic on their login.
                </p>

                {teacherClasses.length === 0 ? (
                  <p className="text-sm text-slate-500">
                    You don&rsquo;t have any classes set up yet. Add one from the Teacher View.
                  </p>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {teacherClasses.map((c) => {
                      const k = classKey(c);
                      const checked = selectedClassKeys.has(k);
                      return (
                        <label
                          key={k}
                          className={`cursor-pointer rounded-full border px-3 py-1 text-sm transition-colors ${
                            checked
                              ? 'border-indigo-500 bg-indigo-500 text-white'
                              : 'border-slate-300 bg-white text-slate-700 hover:border-indigo-400'
                          }`}
                        >
                          <input
                            type="checkbox"
                            className="sr-only"
                            checked={checked}
                            onChange={() => toggleClassKey(k)}
                          />
                          {classLabel(c)}
                        </label>
                      );
                    })}
                  </div>
                )}

                <div className="flex items-center gap-3">
                  <button
                    type="button"
                    onClick={handleSaveAssignments}
                    disabled={isSavingAssignments || isLoadingAssignments || teacherClasses.length === 0}
                    className="rounded-xl bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    {isSavingAssignments ? 'Saving…' : 'Save assignments'}
                  </button>
                  {isLoadingAssignments && <span className="text-xs text-slate-500">Loading…</span>}
                  {showSavedToast && <span className="text-xs text-emerald-600">Saved ✓</span>}
                </div>

                {assignError && (
                  <p className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                    {assignError}
                  </p>
                )}
              </div>
            )}
          </section>
        )}

        {/* History */}
        <section className="rounded-3xl border border-violet-200 bg-white/95 p-6 shadow-sm">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-slate-900">History</h2>
            <button
              type="button"
              onClick={fetchHistory}
              disabled={isLoadingHistory}
              className="flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-violet-700 disabled:opacity-50"
            >
              <RefreshRounded fontSize="small" /> Refresh
            </button>
          </div>

          {historyError && (
            <div className="mt-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
              {historyError}
            </div>
          )}

          {!historyError && (history?.length ?? 0) === 0 && !isLoadingHistory && (
            <p className="mt-3 text-sm text-slate-500">No extractions yet. Upload your first PDF above.</p>
          )}

          {history && history.length > 0 && (
            <ul className="mt-4 divide-y divide-slate-100">
              {history.map((row) => (
                <li key={row.id} className="flex items-center justify-between py-2">
                  <button
                    type="button"
                    onClick={() => handleOpenHistoryItem(row.id)}
                    className="flex-1 text-left text-sm font-medium text-slate-700 hover:text-violet-700"
                  >
                    {row.file_name}
                    <span className="block text-xs text-slate-500">
                      {new Date(row.created_at).toLocaleString()}
                      {row.section_count != null && ` · ${row.section_count} sections`}
                    </span>
                  </button>
                  {statusBadge(row.status)}
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default PdfTopicExtractor;
